import { Dict } from "mixpanel-browser";
import { IAnalyticsProvider } from "../../core/ports/driven/analyticsProvider";
import { reportAppError } from "../../../sentry";
import { localStorageProvider } from "../../../AppController";
const env = import.meta.env;
import { apiOrigin } from "../../../config";

/**
 * Emails of users who should have Mixpanel Session Replay enabled.
 * Compassus users are also allowed by email domain.
 * All emails are compared in lowercase for case-insensitive matching.
 */
const SESSION_REPLAY_ALLOWED_EMAILS: string[] = [
  // Add user emails here, e.g.:
  // "user@example.com",
  "yi+hh@circadia.health",
  "rachel.jorgensen@circadia.health",
  "jon+compassusdemo1@circadia.health",
  "jon@michalcircadia.onmicrosoft.com",
  "Mary.Doggett@compassus.com",
  "SherryEllaine.Echano@compassus.com",
];
const SESSION_REPLAY_ALLOWED_EMAIL_DOMAIN = "@compassus.com";

export type IAnalyticsController = ReturnType<
  typeof AnalyticsControllerFactory
>;

export const AnalyticsControllerFactory = ({
  analyticsProvider,
  circadiaEngineProvider,
}: {
  analyticsProvider: IAnalyticsProvider;
  circadiaEngineProvider: ICircadiaEngineProvider;
}) => {
  try {
    analyticsProvider.init(env.VITE_ANALYTICS_KEY, {
      // api_host: "https://api.mixpanel.com",
      api_host: `${apiOrigin}/patient-centric-app-v2/ce`,
      persistence: "localStorage",
      ignore_dnt: true,
      api_transport: "XHR",
    });
  } catch (error) {
    reportAppError(error as Error, {
      component: "analyticsController",
      action: "init",
      additionalData: {
        error: error as string,
        failedFor: localStorageProvider.get("userEmail") || "unknown",
      },
    });
    console.error("[Analytics] Failed to initialize analytics:", error);
  }

  const resetEvent = () => {
    analyticsProvider.reset();
  };
  const logEvent = (
    eventName: string,
    eventProperties?: Dict,
    identity?: string
  ) => {
    if (identity) {
      analyticsProvider.identify(identity);
    }
    const eventProp: Dict = {
      ...eventProperties,
      appVersion: APP_VERSION,
    };

    if (circadiaEngineProvider.isWindowCircadia()) {
      eventProp.intfv = circadiaEngineProvider.intfv;
      eventProp.plat = circadiaEngineProvider.plat;
    }

    // TODO: Remove before committing - local debug logging
    // console.log(`[Analytics] ${eventName}`, eventProp);
    analyticsProvider.track(eventName, eventProp);
    // const intercomEventProp = { ...eventProp }
    // delete intercomEventProp.token
    // delete intercomEventProp.selectedFacilityId
    // delete intercomEventProp.cUserId
    // webIntercomProvider.trackEvent(eventName, intercomEventProp);
  };

  const timeEvent = (eventName: string) => {
    analyticsProvider.timeEvent(eventName);
  };

  const setPeople = (obj: {
    name: string;
    role: string;
    email: string;
    userID: string;
  }) => {
    analyticsProvider.setPeople(obj);
  };

  const identify = (identity: string) => {
    analyticsProvider.identify(identity);
  };

  // Enhanced error logging that reports to both Mixpanel and Sentry
  const logApiError = (
    error: Error,
    context: {
      method: string;
      url?: string;
      status?: number;
      apiName?: string;
      requestBody?: unknown;
      responseBody?: unknown;
      duration?: number;
    }
  ) => {
    // Log to Mixpanel (existing behavior)
    logEvent("api-failure", {
      apiName: context.apiName || "unknown",
      method: context.method,
      status: context.status,
      api_url: context.url,
      error_message: error.message,
      error_name: error.name,
    });
  };

  const logAppError = (
    error: Error,
    context: {
      component?: string;
      action?: string;
      userId?: string;
      additionalData?: Record<string, unknown>;
    } = {}
  ) => {
    // Log to Mixpanel
    logEvent("app-error", {
      component: context.component || "unknown",
      action: context.action || "unknown",
      error_message: error.message,
      error_name: error.name,
      user_id: context.userId,
      ...context.additionalData,
    });
  };

  /**
   * Start Mixpanel Session Replay for the given user email
   * if they are in the allowed list. This uses manual capture
   * (start_session_recording) so that only targeted users are recorded.
   *
   * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript/javascript-replay
   */
  const startSessionReplay = (email: string) => {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const isAllowed =
        SESSION_REPLAY_ALLOWED_EMAILS.map((allowedEmail) =>
          allowedEmail.toLowerCase()
        ).includes(normalizedEmail) ||
        normalizedEmail.endsWith(SESSION_REPLAY_ALLOWED_EMAIL_DOMAIN);
      console.warn("[Analytics] Session Replay check — allowed:", isAllowed);
      if (isAllowed) {
        analyticsProvider.startSessionRecording();
        console.warn("[Analytics] Session Replay STARTED");
      } else {
        console.warn(
          "[Analytics] Session Replay SKIPPED — user not in allowed list"
        );
      }
    } catch (error) {
      console.error("[Analytics] Failed to start Session Replay:", error);
      reportAppError(error as Error, {
        component: "analyticsController",
        action: "startSessionReplay",
      });
    }
  };

  /**
   * Stop Mixpanel Session Replay recording.
   * Safe to call even if no recording is in progress.
   */
  const stopSessionReplay = () => {
    try {
      analyticsProvider.stopSessionRecording();
      console.warn("[Analytics] Session Replay STOPPED");
    } catch (error) {
      console.error("[Analytics] Failed to stop Session Replay:", error);
    }
  };

  /**
   * Get session recording properties to attach to server-side events.
   * Returns { $mp_replay_id: string } if a replay is in progress, otherwise {}.
   */
  const getSessionReplayProperties = () => {
    try {
      return analyticsProvider.getSessionRecordingProperties();
    } catch {
      return {};
    }
  };

  return {
    resetEvent,
    logEvent,
    timeEvent,
    setPeople,
    identify,
    logApiError,
    logAppError,
    startSessionReplay,
    stopSessionReplay,
    getSessionReplayProperties,
  };
};
